// Copyright © 2025 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { Fragment, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import copyToClipboard from 'copy-to-clipboard';
import { func, shape, string } from 'prop-types';
import {
  IconButton,
  Typography,
  withStyles,
} from '@material-ui/core';

import styles from './styles';
import { ALERT_SUCCESS } from "../../../../../constants";
import { getI18n } from "../../../../../utils/intl";
import { DetailsPanelContainer, FormDialog, FormSelect, FormTextField } from "../../../../../components";

const copy = ({
  protocol,
  hostName,
  portNumber,
  notifyMessages,
  copyMessage,
}) => {
  notifyMessages(ALERT_SUCCESS, copyMessage);
  copyToClipboard(`${protocol}${hostName}:${portNumber}`);
  setTimeout(() => notifyMessages('', ''), 3000);
};

const copyProxyURL = ({
  url,
  notifyMessages,
  copyMessage,
}) => {
  notifyMessages(ALERT_SUCCESS, copyMessage);
  copyToClipboard(url);
  setTimeout(() => notifyMessages('', ''), 3000);
};

const UUID_HTTP = 'http';
const UUID_HTTPS = 'https';

export const ProxyURLEditor = ({
  classes = {},
  effectiveURL = "",
  onUpdate,
  proxyUrl = "",
  portNumber = "",
  notifyMessages,
  copyMessage,
}) => {
  const intl = getI18n(useIntl());
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedProtocol, setSelectedProtocol] = useState(UUID_HTTPS);
  const httpLabel = intl.getI18nMessage('label.proxy.details.page.overview.proxy.protocol.http');
  const httpsLabel = intl.getI18nMessage('label.proxy.details.page.overview.proxy.protocol.https');
  const [hostName, setHostName] = useState();
  const [updatedPortNumber, setUpdatedPortNumber] = useState();
  const [isInvalidPortNumber, setIsInvalidPortNumber] = useState(false);
  const [isInvalidHostName, setIsInvalidHostName] = useState(false);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);

  const handleCancel = () => {
    if (proxyUrl.startsWith(httpLabel)) {
      setSelectedProtocol(UUID_HTTP);
    } else {
      setSelectedProtocol(UUID_HTTPS);
    }
    setHostName(proxyUrl.replace(httpLabel, '').replace(httpsLabel, ''));
    setUpdatedPortNumber(portNumber);
    setShowEditDialog(false);
  };

  const handleSubmit = () => {
    const protocolText = selectedProtocol === UUID_HTTPS ? httpsLabel : httpLabel;
    onUpdate({
      host: `${protocolText}${hostName}`,
      portNumber: updatedPortNumber,
    });
    setShowEditDialog(false);
  };

  const handleProtocolChange = (val) => {
    setSelectedProtocol(val);
    if (val === UUID_HTTPS) {
      setUpdatedPortNumber(443);
    } else if (val === UUID_HTTP) {
      setUpdatedPortNumber(80);
    }
  }

  const handlePortNumberChange = (val) => {
    if (val === '') {
      setIsInvalidPortNumber(false);
    } else if (Number.isNaN(val)) {
      setIsInvalidPortNumber(true);
    } else if (val >= 1 && val <= 65535) {
      setIsInvalidPortNumber(false);
    } else {
      setIsInvalidPortNumber(true);
    }
    setUpdatedPortNumber(val);
  };

  const handleHostNameChange = (val) => {
    const portPattern =
      /:([1-9][0-9]{0,3}|[1-5][0-9]{4}|6[0-4][0-9]{3}|65[0-4][0-9]{2}|655[0-2][0-9]|6553[0-5])$/i;
    if (!val) {
      setIsInvalidHostName(true);
    } else if (val.startsWith(httpLabel) ||
      val.startsWith(httpsLabel) ||
      val.match(portPattern)) {
      setIsInvalidHostName(true);
    } else {
      setIsInvalidHostName(false);
    }
    setHostName(val);
  };

  useEffect(() => {
    setUpdatedPortNumber(portNumber);
  }, [portNumber]);

  const getProtocolFromProxyURL = (url) => {
    if (url.startsWith(httpLabel)) {
      return UUID_HTTP;
    }
    return UUID_HTTPS;
  };

  const getHostNameFromProxyURL = (url) => url.replace(httpLabel, '').replace(httpsLabel, '');

  useEffect(() => {
    setSelectedProtocol(getProtocolFromProxyURL(proxyUrl));
    setHostName(getHostNameFromProxyURL(proxyUrl));
  }, [proxyUrl]);

  const PROXY_DETAILS_PROTOCOL_OPTIONS = [
    {
      uuid: UUID_HTTP,
      text: intl.getI18nMessage('label.proxy.details.page.overview.proxy.protocol.http'),
    }, {
      uuid: UUID_HTTPS,
      text: intl.getI18nMessage('label.proxy.details.page.overview.proxy.protocol.https'),
    },
  ];

  useEffect(() => {
    if (isInvalidHostName || isInvalidPortNumber) {
      setSubmitButtonDisabled(true);
    } else {
      const initialProtocol = getProtocolFromProxyURL(proxyUrl);
      const initialHostName = getHostNameFromProxyURL(proxyUrl);
      if ((hostName) && (initialProtocol !== selectedProtocol ||
        initialHostName !== hostName ||
        // eslint-disable-next-line eqeqeq
        portNumber != updatedPortNumber)) {
        setSubmitButtonDisabled(false);
      } else {
        setSubmitButtonDisabled(true);
      }
    }
  }, [isInvalidHostName,
    isInvalidPortNumber,
    proxyUrl,
    selectedProtocol,
    hostName,
    portNumber,
    updatedPortNumber,
  ]);

  return (
    <Fragment>
      <FormDialog
        isOpen={showEditDialog}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
        cancelText={intl.getI18nMessage('label.cancel.button')}
        paperClass={classes.paperClass}
        submitText={intl.getI18nMessage('label.save.button')}
        title={intl.getI18nMessage('label.proxy.details.page.overview.proxy.url.with.port.title')}
        additionalBtnText={intl.getI18nMessage(
          'label.copy.button')}
        onAdditionalBtnClick={() => copy({
          protocol: selectedProtocol === UUID_HTTPS ? httpsLabel : httpLabel,
          hostName,
          portNumber: updatedPortNumber,
          notifyMessages,
          copyMessage,
        })}
        submitButtonDisabled={submitButtonDisabled}
      >
        <FormSelect
          name={intl.getI18nMessage('label.proxy.details.page.overview.url.form.dialog.protocol')}
          id={'proxy-details-url-protocol-field'}
          data-apim-test={'proxy-details-url-protocol-field'}
          value={selectedProtocol}
          handleChange={handleProtocolChange}
          data={PROXY_DETAILS_PROTOCOL_OPTIONS}
          noNoneOption
          noNativeSelect
        />
        <FormTextField
          name={intl.getI18nMessage('label.proxy.details.page.overview.url.form.dialog.host.name')}
          id={'proxy-details-url-field'}
          data-apim-test={'proxy-details-url-field'}
          variant="standard"
          value={hostName}
          handleChange={handleHostNameChange}
          error={isInvalidHostName}
          helperText={intl.getI18nMessage('label.proxy.details.page.overview.url.form.dialog.host.name.helptext')}
        />
        <FormTextField
          name={intl.getI18nMessage('label.proxy.details.page.overview.url.form.dialog.port')}
          id={'proxy-details-port-field'}
          data-apim-test={'proxy-details-port-field'}
          variant="standard"
          value={updatedPortNumber}
          handleChange={handlePortNumberChange}
          error={isInvalidPortNumber}
          helperText={intl.getI18nMessage('label.proxy.details.page.overview.url.form.dialog.port.helptext')}
          optional
          placeholder={selectedProtocol === UUID_HTTPS ? '443' : '80'}
        />
      </FormDialog>
      <IconButton
        className={classes.copyUrl}
        id={'proxy-details-edit-button'}
        data-apim-test={'proxy-details-edit-button'}
        onClick={() => setShowEditDialog(true)}
      >
        <Typography variant="body1">
          {intl.getI18nMessage('label.edit')}
        </Typography>
      </IconButton>
      <IconButton
        className={classes.copyUrl}
        id={'proxy-details-copy-button'}
        data-apim-test={'proxy-details-copy-button'}
        onClick={() => copyProxyURL({ copyMessage, notifyMessages, url: effectiveURL })}
      >
        <Typography variant="body1">
          {intl.getI18nMessage('label.copy.button')}
        </Typography>
      </IconButton>
    </Fragment>
  );
};

ProxyURLEditor.propTypes = {
  classes: shape({}),
  effectiveURL: string,
  notifyMessages: func,
  onUpdate: func,
  proxyUrl: string,
  portNumber: string,
  copyMessage: string,
};
ProxyURLEditor.displayName = 'ProxyURLEditor';
export const ProxyURLContainer = ({
  classes = {},
  id,
  proxyUrl = '',
  portNumber,
  onUpdate,
  title,
  notifyMessages,
  copyMessage,
}) => {
  const intl = getI18n(useIntl());
  const httpLabel = intl.getI18nMessage('label.proxy.details.page.overview.proxy.protocol.http');
  const httpsLabel = intl.getI18nMessage('label.proxy.details.page.overview.proxy.protocol.https');
  let url = proxyUrl;
  const httpPort = 80;
  const httpsPort = 8443;
  if (!url.startsWith(httpLabel) && !url.startsWith(httpsLabel) ) {
    url = `${httpsLabel}${url}`;
  }
  if(!portNumber) {
    if(url.startsWith(httpsLabel)) {
      portNumber = httpsPort;
    } else if(url.startsWith(httpLabel)) {
      portNumber = httpPort;
    }
  }
  const subTitle = `${url}:${portNumber}`;
  return (
    <DetailsPanelContainer
      id={id}
      title={title ? title : intl.getI18nMessage(
        'label.proxy.details.page.overview.proxy.url')}
      subTitle={subTitle}
      smallFont
      content={
        <ProxyURLEditor
          classes={classes}
          effectiveURL={subTitle}
          onUpdate={onUpdate}
          portNumber={portNumber}
          proxyUrl={proxyUrl}
          notifyMessages={notifyMessages}
          copyMessage={copyMessage}
        />
      }
    />
  );
}

export default (
  withStyles(styles)
)(ProxyURLContainer);

ProxyURLContainer.propTypes = {
  classes: shape({}),
  id: string,
  proxyUrl: string,
  portNumber: string,
  notifyMessages: func,
  onUpdate: func,
  title: string,
  copyMessage: string,
};
ProxyURLContainer.displayName = 'ProxyURLContainer';
